import { VisitType } from './visit-type.enum'

export enum AppointmentStatus {
  Requested = 0,
  Approved = 1,
  Completed = 2,
  Canceled = 3,
  Rejected = 4,
  NoShow = 5,
  PatientCancel = 6,
  Expired = 7,
  SpecialistRescheduled = 8,
  DesCanceled = 9,
}

export enum AppointmentPaymentStatus {
  None = 0,
  Processing = 1,
  Paid = 2,
  Canceled = 3,
}

export enum AppointmentChargeStatus {
  None = 0,
  Processing = 1,
  Paid = 2,
  Failed = 3,
  Refunded = 4,
}

/**
 * What ultimately happened to the $50 approval fee.
 *
 * A different question from `AppointmentChargeStatus`, which only says whether
 * the charge settled. `RefundOwed` is the one that needs acting on: a refund
 * failed and the patient is still waiting on their money.
 */
export enum ApprovalFeeOutcome {
  None = 0,
  Refunded = 1,
  RetainedLateCancellation = 2,
  RetainedNoShow = 3,
  RefundOwed = 4,
  RefundedExternally = 5,
  Disputed = 6,
}

export enum AppointmentTimeZone {
  EST = 'EST',
}

// ── Labels ──────────────────────────────────────────────────────────

const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  [AppointmentStatus.Requested]: 'Requested',
  [AppointmentStatus.Approved]: 'Approved',
  [AppointmentStatus.Completed]: 'Completed',
  [AppointmentStatus.Canceled]: 'Canceled',
  [AppointmentStatus.Rejected]: 'Rejected',
  [AppointmentStatus.NoShow]: 'No Show',
  [AppointmentStatus.PatientCancel]: 'Patient Canceled',
  [AppointmentStatus.Expired]: 'Expired',
  [AppointmentStatus.SpecialistRescheduled]: 'Specialist Rescheduled',
  [AppointmentStatus.DesCanceled]: 'DES Canceled',
}

const PAYMENT_STATUS_LABELS: Record<AppointmentPaymentStatus, string> = {
  [AppointmentPaymentStatus.None]: 'None',
  [AppointmentPaymentStatus.Processing]: 'Processing',
  [AppointmentPaymentStatus.Paid]: 'Paid',
  [AppointmentPaymentStatus.Canceled]: 'Canceled',
}

const CHARGE_STATUS_LABELS: Record<AppointmentChargeStatus, string> = {
  [AppointmentChargeStatus.None]: 'Not charged',
  [AppointmentChargeStatus.Processing]: 'Processing',
  [AppointmentChargeStatus.Paid]: 'Paid',
  [AppointmentChargeStatus.Failed]: 'Failed',
  [AppointmentChargeStatus.Refunded]: 'Refunded',
}

const APPROVAL_FEE_OUTCOME_LABELS: Record<ApprovalFeeOutcome, string> = {
  [ApprovalFeeOutcome.None]: '',
  [ApprovalFeeOutcome.Refunded]: 'Refunded',
  [ApprovalFeeOutcome.RetainedLateCancellation]: 'Late cancellation fee kept',
  [ApprovalFeeOutcome.RetainedNoShow]: 'No-show fee kept',
  [ApprovalFeeOutcome.RefundOwed]: 'Refund owed',
  // Same thing to a reader as Refunded; they differ only in who issued it.
  [ApprovalFeeOutcome.RefundedExternally]: 'Refunded',
  [ApprovalFeeOutcome.Disputed]: 'Chargeback',
}

const VISIT_TYPE_LABELS: Record<VisitType, string> = {
  [VisitType.InPerson]: 'In-person',
  [VisitType.Telemedicine]: 'Telemedicine',
  [VisitType.Both]: 'In-person or Telemedicine',
}

export const getAppointmentStatusLabel = (status: AppointmentStatus | null | undefined): string =>
  status != null ? APPOINTMENT_STATUS_LABELS[status] ?? '' : ''

export const getPaymentStatusLabel = (status: AppointmentPaymentStatus | string | null | undefined): string => {
  if (status == null) return ''
  if (typeof status === 'string') return formatSnakeCase(status)
  return PAYMENT_STATUS_LABELS[status] ?? ''
}

export const getChargeStatusLabel = (
  status: AppointmentChargeStatus | null | undefined,
): string => (status != null ? CHARGE_STATUS_LABELS[status] ?? '' : '')

export const getApprovalFeeOutcomeLabel = (
  outcome: ApprovalFeeOutcome | null | undefined,
): string => (outcome != null ? APPROVAL_FEE_OUTCOME_LABELS[outcome] ?? '' : '')

export const getVisitTypeLabel = (visitType: VisitType | number | null | undefined): string =>
  visitType != null ? VISIT_TYPE_LABELS[visitType as VisitType] ?? '' : ''

export const getStripeIntentStatusLabel = (status: string | null | undefined): string =>
  status?.trim() ? formatSnakeCase(status) : ''

function formatSnakeCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// ── Colors ──────────────────────────────────────────────────────────

type StatusColor = 'warning' | 'info' | 'success' | 'error' | 'neutral'

const APPOINTMENT_STATUS_COLORS: Record<AppointmentStatus, StatusColor> = {
  [AppointmentStatus.Requested]: 'warning',
  [AppointmentStatus.Approved]: 'info',
  [AppointmentStatus.Completed]: 'success',
  [AppointmentStatus.Canceled]: 'error',
  [AppointmentStatus.Rejected]: 'neutral',
  [AppointmentStatus.NoShow]: 'warning',
  [AppointmentStatus.PatientCancel]: 'error',
  [AppointmentStatus.Expired]: 'neutral',
  [AppointmentStatus.SpecialistRescheduled]: 'info',
  [AppointmentStatus.DesCanceled]: 'error',
}

const PAYMENT_STATUS_COLORS: Record<AppointmentPaymentStatus, StatusColor> = {
  [AppointmentPaymentStatus.None]: 'neutral',
  [AppointmentPaymentStatus.Processing]: 'info',
  [AppointmentPaymentStatus.Paid]: 'success',
  [AppointmentPaymentStatus.Canceled]: 'error',
}

const CHARGE_STATUS_COLORS: Record<AppointmentChargeStatus, StatusColor> = {
  [AppointmentChargeStatus.None]: 'neutral',
  [AppointmentChargeStatus.Processing]: 'info',
  [AppointmentChargeStatus.Paid]: 'success',
  [AppointmentChargeStatus.Failed]: 'error',
  [AppointmentChargeStatus.Refunded]: 'info',
}

const APPROVAL_FEE_OUTCOME_COLORS: Record<ApprovalFeeOutcome, StatusColor> = {
  [ApprovalFeeOutcome.None]: 'neutral',
  [ApprovalFeeOutcome.Refunded]: 'success',
  [ApprovalFeeOutcome.RetainedLateCancellation]: 'warning',
  [ApprovalFeeOutcome.RetainedNoShow]: 'warning',
  [ApprovalFeeOutcome.RefundOwed]: 'error',
  [ApprovalFeeOutcome.RefundedExternally]: 'success',
  [ApprovalFeeOutcome.Disputed]: 'error',
}

export const getAppointmentStatusColor = (status: AppointmentStatus): StatusColor =>
  APPOINTMENT_STATUS_COLORS[status]

export const getPaymentStatusColor = (status: AppointmentPaymentStatus): StatusColor =>
  PAYMENT_STATUS_COLORS[status]

export const getChargeStatusColor = (status: AppointmentChargeStatus): StatusColor =>
  CHARGE_STATUS_COLORS[status] ?? 'neutral'

export const getApprovalFeeOutcomeColor = (outcome: ApprovalFeeOutcome): StatusColor =>
  APPROVAL_FEE_OUTCOME_COLORS[outcome] ?? 'neutral'

// ── Status notices ──────────────────────────────────────────────────

export type NoticeTone = 'info' | 'success' | 'warning' | 'danger'
export type StatusNotice = { tone: NoticeTone; message: string }

export const NOTICE_CLASSES: Record<NoticeTone, { wrapper: string; text: string }> = {
  info: {
    wrapper: 'bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-xl p-4',
    text: 'text-sm text-blue-900 dark:text-blue-100',
  },
  success: {
    wrapper: 'bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4',
    text: 'text-sm text-emerald-900 dark:text-emerald-100',
  },
  warning: {
    wrapper: 'bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-xl p-4',
    text: 'text-sm text-amber-900 dark:text-amber-100',
  },
  danger: {
    wrapper: 'bg-rose-50 dark:bg-rose-950 border border-rose-200 dark:border-rose-800 rounded-xl p-4',
    text: 'text-sm text-rose-900 dark:text-rose-100',
  },
}

export const PATIENT_STATUS_NOTICES: Record<AppointmentStatus, StatusNotice> = {
  [AppointmentStatus.Requested]: { tone: 'info', message: "Your request is under review. Once the doctor confirms, we'll charge the $50 approval fee to your saved card and email you the confirmation." },
  [AppointmentStatus.Approved]: { tone: 'success', message: 'Appointment confirmed — the $50 approval fee has been charged to your card. Any remaining balance is charged after your visit.' },
  [AppointmentStatus.Completed]: { tone: 'success', message: 'Appointment completed. Payment complete.' },
  [AppointmentStatus.Canceled]: { tone: 'danger', message: 'This appointment has been canceled.' },
  [AppointmentStatus.Rejected]: { tone: 'danger', message: 'This appointment has been canceled.' },
  [AppointmentStatus.NoShow]: { tone: 'danger', message: 'This appointment was marked as no-show.' },
  [AppointmentStatus.PatientCancel]: { tone: 'danger', message: 'This appointment has been canceled.' },
  [AppointmentStatus.Expired]: { tone: 'danger', message: 'This appointment request has expired.' },
  [AppointmentStatus.SpecialistRescheduled]: { tone: 'info', message: 'This appointment was rescheduled by the doctor. Please check your email for the new time.' },
  [AppointmentStatus.DesCanceled]: { tone: 'danger', message: 'This appointment has been canceled.' },
}

export const ADMIN_STATUS_NOTICES: Record<AppointmentStatus, StatusNotice> = {
  [AppointmentStatus.Requested]: { tone: 'info', message: "Confirming charges the $50 approval fee to the patient's saved card and approves the appointment. If they have no card on file, generate a save-card link first." },
  [AppointmentStatus.Approved]: { tone: 'success', message: '$50 approval fee collected. Completing the appointment charges the remaining balance, or mark as paid if collected offline.' },
  [AppointmentStatus.Completed]: { tone: 'success', message: 'This appointment has been completed successfully.' },
  [AppointmentStatus.Canceled]: { tone: 'danger', message: 'This appointment has been canceled.' },
  [AppointmentStatus.Rejected]: { tone: 'danger', message: 'This appointment has been canceled.' },
  [AppointmentStatus.NoShow]: { tone: 'danger', message: 'Patient did not attend. The $50 approval fee already charged is kept as the no-show fee; nothing further is charged.' },
  [AppointmentStatus.PatientCancel]: { tone: 'danger', message: 'This appointment was canceled by the patient.' },
  [AppointmentStatus.Expired]: { tone: 'danger', message: 'This appointment request has expired.' },
  [AppointmentStatus.SpecialistRescheduled]: { tone: 'info', message: 'This appointment was rescheduled by the doctor.' },
  [AppointmentStatus.DesCanceled]: { tone: 'danger', message: 'This appointment has been canceled.' },
}

export const DATE_RANGE_LABELS: Record<string, string> = {
  today: 'Today',
  tomorrow: 'Tomorrow',
  'this-week': 'This Week',
  'this-month': 'This Month',
}

export const getDateRangeParams = (range: string) => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  switch (range) {
    case 'today': return { fromDate: today.toISOString(), toDate: new Date(today.getTime() + 86400000 - 1).toISOString() }
    case 'tomorrow': { const t = new Date(today); t.setDate(t.getDate() + 1); return { fromDate: t.toISOString(), toDate: new Date(t.getTime() + 86400000 - 1).toISOString() } }
    case 'this-week': { const s = new Date(today); s.setDate(today.getDate() - today.getDay()); const e = new Date(s); e.setDate(s.getDate() + 6); e.setHours(23,59,59,999); return { fromDate: s.toISOString(), toDate: e.toISOString() } }
    case 'this-month': { const s = new Date(today.getFullYear(), today.getMonth(), 1); const e = new Date(today.getFullYear(), today.getMonth() + 1, 0); e.setHours(23,59,59,999); return { fromDate: s.toISOString(), toDate: e.toISOString() } }
    default: return {}
  }
}

// ── Filter options ──────────────────────────────────────────────────

export type FilterOption = { value: string; label: string }

const appointmentStatusOptions = Object.values(AppointmentStatus).filter((v): v is AppointmentStatus => typeof v === 'number')
const paymentStatusOptions = Object.values(AppointmentPaymentStatus).filter((v): v is AppointmentPaymentStatus => typeof v === 'number')

export const STATUS_FILTER_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All Statuses' },
  ...appointmentStatusOptions.map((v) => ({ value: String(v), label: getAppointmentStatusLabel(v) })),
]

export const PAYMENT_FILTER_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All Payments' },
  ...paymentStatusOptions.map((v) => ({ value: String(v), label: getPaymentStatusLabel(v) })),
]

const approvalFeeOutcomeOptions = Object.values(ApprovalFeeOutcome).filter(
  (v): v is ApprovalFeeOutcome => typeof v === 'number' && v !== ApprovalFeeOutcome.None,
)

export const APPROVAL_FEE_OUTCOME_FILTER_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All Fee Outcomes' },
  ...approvalFeeOutcomeOptions.map((v) => ({
    value: String(v),
    label: getApprovalFeeOutcomeLabel(v),
  })),
]

export const DATE_RANGE_FILTER_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All Dates' },
  ...Object.entries(DATE_RANGE_LABELS).map(([value, label]) => ({ value, label })),
]
